import { forwardRef } from 'react'
import tw, { styled } from 'twin.macro'

import { ChevronDown } from '../Icons'
import {
  focusStyles,
  inputInnerStyles,
  inputWrapperErrorStyles,
} from '../sharedStyles'

export const DropdownWrapper = tw.div`
  relative
`

export const DropdownButton = styled.button.attrs({ type: 'button' })(
  ({
    $isPlaceholder,
    $hasError,
  }: {
    $isPlaceholder?: boolean
    $hasError?: boolean
  }) => [
    focusStyles,
    inputInnerStyles,
    tw`
      bg-default-subdued
      border
      border-inverted-subdued
      border-solid
      flex
      justify-between
      items-center
      rounded-md
      text-left
      w-full
      disabled:opacity-50
    `,
    $isPlaceholder && tw`text-gray-400`,
    $hasError && inputWrapperErrorStyles,
  ],
)

export const DropdownList = styled.div(
  ({ $isVisible }: { $isVisible?: boolean }) => [
    focusStyles,
    tw`
      w-full
      border border-default-emphasis border-solid
      py-2
      rounded
      shadow-md
      bg-default-subdued
      z-10
      font-brand text-left
      overflow-y-auto
      max-h-56
      space-y-1
    `,
    $isVisible === false && tw`hidden`,
  ],
)

const ItemRow = styled.div(
  ({
    $isSelected,
    $isHighlighted,
    $hasSubtitle,
    $hasStartSlot,
    $hasEndSlot,
    $isAction,
    disabled,
  }: {
    $isSelected: boolean
    $isHighlighted: boolean
    $hasSubtitle: boolean
    $hasStartSlot: boolean
    $hasEndSlot: boolean
    $isAction: boolean
    disabled?: boolean
  }) => [
    tw`
      flex justify-between
      mx-2 py-2
      rounded
      cursor-pointer outline-none leading-none
      border-2 border-transparent`,
    $hasStartSlot ? tw`pl-3` : tw`px-4`,
    $hasEndSlot ? tw`pr-3` : tw`pr-4`,
    $hasSubtitle ? tw`items-start` : tw`items-center`,
    $isHighlighted && !$isAction && tw`border-default-neutral`,
    $isHighlighted && $isAction && tw`bg-brand-subdued`,
    $isHighlighted && $isSelected && tw`border-default-emphasis`,
    $isSelected && !disabled && tw`bg-brand-subdued `,
    disabled && tw`opacity-50`,
  ],
)

const LeftSlot = tw.div`mr-3`
const ItemContent = tw.div`flex-1`
const RightSlot = tw.div`ml-3`

export const DropdownTitle = styled.div(
  ({
    $isAction = false,
    $isSelected = false,
  }: {
    $isAction?: boolean
    $isSelected?: boolean
  }) => [
    tw`text-sm-brand`,
    $isSelected || $isAction
      ? tw`text-brand-neutral`
      : tw`text-default-emphasis`,
  ],
)

export const DropdownSubtitle = styled.div(
  ({
    $isAction = false,
    $isSelected = false,
  }: {
    $isAction?: boolean
    $isSelected?: boolean
  }) => [
    tw` text-xs-brand`,
    $isSelected || $isAction ? tw`text-brand-neutral` : tw`text-default-muted`,
  ],
)

type DropdownItemsProps = {
  isSelected?: boolean
  isAction?: boolean
  isHighlighted?: boolean
  hasSubtitle?: boolean
  startSlot?: string | React.ReactNode
  endSlot?: string | React.ReactNode
  disabled?: boolean
  children: React.ReactNode
}

// Downshift passes a ref for each DropdownItem
// so we need forwardRef to support it
export const DropdownListItem = forwardRef<HTMLDivElement, DropdownItemsProps>(
  function DropdownItem(
    {
      isSelected,
      isHighlighted,
      hasSubtitle,
      children,
      startSlot,
      endSlot,
      isAction,
      disabled,
      ...rest
    },
    ref,
  ) {
    return (
      <ItemRow
        ref={ref}
        disabled={disabled}
        $isAction={Boolean(isAction)}
        $isHighlighted={Boolean(isHighlighted)}
        $isSelected={Boolean(isSelected)}
        $hasSubtitle={Boolean(hasSubtitle)}
        $hasStartSlot={Boolean(startSlot)}
        $hasEndSlot={Boolean(endSlot)}
        {...rest}
      >
        {startSlot && <LeftSlot>{startSlot}</LeftSlot>}
        <ItemContent>{children}</ItemContent>
        {endSlot && <RightSlot>{endSlot}</RightSlot>}
      </ItemRow>
    )
  },
)

export const DropdownSelected = tw.div`flex-1`

export const DropdownArrow = tw(ChevronDown)`
  text-default-muted
`
